import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector, LOCALE_ID, inject, signal } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ComponentItem } from '@models/component';
import { FilterConfig, FilterTypes } from '@models/filter-config';
import { CookieService } from 'ngx-cookie-service';
import { msalConfig } from '../msal.config';
import { AuthService } from './auth.service';
import { LocalStorageService } from './local-storage.service';
import { MessageService } from './message.service';

/**
 * service, which shares methods to be used in various components
 */
@Injectable( {
    providedIn: 'root'
} )
export class SharedService {
    #dialog = inject( MatDialog );
    locale = inject( LOCALE_ID );

    /** ignore in coverage */
    #authService = inject( AuthService );
    #cookieService = inject( CookieService );
    #injector = inject( Injector );
    #localStorageService = inject( LocalStorageService );
    #messageService = inject( MessageService );
    #router = inject( Router );
    #window = inject( Window ) as Window;

    consentChecked = signal( this.#cookieService.check( 'scope.consent' ) );

    /**
     * check if refreshToken is still valid
     * @returns Promise<string> - return refreshToken when valid
     * @throws Error when token is invalid or not found
     */
    async checkTokenValidity(): Promise<string> {

        const tokenIssuerHost = msalConfig.auth.knownAuthorities![ 0 ];
        const tokenKey = `${tokenIssuerHost}|refreshtoken`;

        // Find refresh token in sessionStorage
        const refreshTokenKey = Object.keys( sessionStorage )
            .find( key => key.includes( tokenKey ) );

        if ( !refreshTokenKey ) {
            throw new Error( 'Refresh token not found' );
        }

        const refreshTokenObj = sessionStorage.getItem( refreshTokenKey );
        if ( !refreshTokenObj ) {
            throw new Error( 'Invalid refresh token data' );
        }

        const refreshtoken = JSON.parse( refreshTokenObj! );

        // check if token is still valid with 10 hour buffer to expiration date
        const now = Date.now();
        const refreshExpireOn = refreshtoken.expiresOn * 1000; // convert to milliseconds
        const timeOffset = 1000 * 60 * 60 * 10; // time offset before invalidation
        const stillValid = now <= refreshExpireOn - timeOffset;

        return stillValid ? refreshtoken.secret : Promise.reject( 'Token validation failed' );
    }

    // set login cookie to enable re-login after logout and redirect from MS Azure
    prepareSessionRenewal() {
        this.#cookieService.set( 'login', 'true', undefined, '/' );
        this.#authService.logout();
    }

    /**
     * helper function to start Scope Client with required parameters
     *
     * when scope instance version is greater than 24.6.26 refreshtoken is read
     * from localStorage, otherwise accessToken from {@link AuthService} is used.
     *
     * when refreshToken is used, then the expire timestamp is checked
     * if token will expire in less than offset time, the user will be
     * automatically logged out and in again to renew the session.
     *
     * if Scope client is installed, the client will start with that URL.
     * @param {string} version - version of scope server, defaults to 1.0.0
     * @param {string} url - url to scope server
     * @returns {boolean} Promise as boolean
     */
    async launchScope( version = '1.0.0', url: string ): Promise<boolean> {
        return this.checkTokenValidity()
            .then( async ( validToken ) => {
                // always trigger token renewal to prevent expired session
                const token = await this.#authService.retrieveAccessToken();

                let params;
                const useRefreshToken = this.checkVersion( version, '24.6.26' ) >= 0;
                if ( useRefreshToken ) {
                    const tokenIssuerHost = msalConfig.auth.knownAuthorities![ 0 ];
                    params = { tokenIssuerHost, refreshtoken: validToken };
                } else {
                    params = { accesstoken: token };
                }

                const account = this.#localStorageService.load( 'account' );
                if ( account ) {
                    const payload = {
                        ...params,
                        preferredLanguage: this.#cookieService.get( 'scope.language' ) || account.preferredLanguage,
                        formatLocale: account.formatLocale
                    };
                    const query = new URLSearchParams( payload );

                    // Use Angular's Router for navigation to preserve app state - which is a workaround.
                    if ( url && url.startsWith( this.#window.location.origin ) ) {
                        this.#router.navigateByUrl( `${url}?${query}` );
                    }
                    // Otherwise, if the target URL is outside the Angular app, a hard redirect is required.
                    else {
                        this.#window.location.href = `${url}?${query}`; // Hard redirect for external URLs
                    }
                }
                return true;
            } )
            .catch( () => {
                this.prepareSessionRenewal();
                return false;
            } );
    }

    /**
     * helper function to compare given version strings
     * - 0 when first version is same as second
     * - 1 when first version is higher than second
     * - -1 when first version is lower than second
     * @param a version a
     * @param b version b
     * @returns {number} value depending on comparison result
     */
    checkVersion( a: string, b: string ): number {
        const x = a.split( '.' ).map( ( e ) => parseInt( e, 10 ) );
        const y = b.split( '.' ).map( ( e ) => parseInt( e, 10 ) );

        for ( const i in x ) {
            y[ i ] = y[ i ] || 0;
            if ( x[ i ] === y[ i ] ) {
                continue;
            } else if ( x[ i ] > y[ i ] ) {
                return 1;
            } else {
                return -1;
            }
        }
        return y.length > x.length ? -1 : 0;
    }

    /** navigate to instance details
     * @param id {string} - instance id
     */
    navigateToInstance( id: string ) {
        this.#router.navigate( [ 'instances', id ] );
    }

    /** end of session handling */
    handleSessionExpired() {
        /** show error message */
        const line = $localize`:@@error-msg-loggout-line:Your session has expired.`;
        const line2 = $localize`:@@error-msg-loggout-line2:Click on the OK button to renew your session.`;
        const supportMsg = `${line}\n
                    ${line2}`;

        const msg = {
            text: supportMsg
        };
        const snackBarRef = this.#messageService.error( msg );

        // trigger logout on dismiss (action clicked)
        snackBarRef.afterDismissed().subscribe( () => {
            this.#authService.logout();
        } );
    }

    /**
     * handle (404) errors occuring in network stack / on server side
     * @param error - http error object
     * @returns boolean - flag, if error status has been handled or not
     */
    handleServerSideError( error: HttpErrorResponse ): boolean {
        let handled = false;

        switch ( error.status ) {
            case 404:
                this.#router.navigate( [ '404' ] );
                handled = true;
                break;
        }
        return handled;
    }

    /**
     * helper function to show preview in a sidebar panel
     * parameter which includes the preview component class and data to be shown
     * NOTE: after closed action currently not implemented - no use case
     * @param {ComponentItem} component
     */
    handleShowSidebar( component: ComponentItem ) {
        const sidepanelConfig = new MatDialogConfig();
        sidepanelConfig.data = component.data;
        sidepanelConfig.panelClass = [ 'sidepanel', 'sidepanel--fade-in-right' ];
        sidepanelConfig.injector = this.#injector;
        sidepanelConfig.autoFocus = 'dialog';

        this.#dialog.open( component.type, sidepanelConfig );
    }

    filters = signal<FilterTypes | undefined>( undefined );

    /**
     * helper function to show dialog when exiting unsaved changes
     * @param {ComponentItem} component
     */
    handleFilterSidepanel( component: ComponentItem ) {
        const filterConfig = new MatDialogConfig<FilterConfig>();
        filterConfig.data = component.data;
        filterConfig.panelClass = [ 'sidepanel', 'sidepanel--fade-in-right' ];
        filterConfig.injector = this.#injector;
        filterConfig.autoFocus = 'dialog';

        const dialogRef = this.#dialog.open( component.type, filterConfig );

        dialogRef.afterClosed().subscribe( ( selectedFilters ) =>
            this.filters.set( selectedFilters ) );
    }

    /**
     * format bytes
     * @param bytes (File size in bytes)
     * @param decimals (Decimals point)
     * @returns {string} formatted string of bytes
     */
    formatBytes( bytes: number, decimals = 0 ): string {
        if ( bytes === 0 ) {
            return '0 Bytes';
        }
        const k = 1024;
        const dm = decimals <= 0 ? 0 : decimals || 2;
        const sizes = [ 'Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB' ];
        const i = Math.floor( Math.log( bytes ) / Math.log( k ) );
        return parseFloat( ( bytes / Math.pow( k, i ) ).toFixed( dm ) ) + ' ' + sizes[ i ];
    }
}
